import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { avatarColors } from "Infrastructure/Themes/Colors";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

export interface BlDetailSectionItemLabel {
  text: string;
  /** Rendered inline after text in secondary color (e.g. "(Natrénováno: Čeština)"). */
  secondaryText?: string;
  /** Native HTML title attribute — shows full text on hover, useful when text is truncated. */
  title?: string;
  /** If provided, only the text is clickable (underline on hover). */
  onClick?: () => void;
}

export interface BlDetailSectionItem {
  id: string | number;
  startAdornment?: React.ReactNode;
  label: BlDetailSectionItemLabel;
  endAdornment?: React.ReactNode;
  /** If provided, the entire row is clickable (brightness hover). */
  onClick?: () => void;
}

export interface BlDetailSectionProps {
  title: string;
  /** Header-right slot — caller builds the button with icon, label, and handler. */
  action?: React.ReactNode;
  items: BlDetailSectionItem[] | undefined;
  isLoading?: boolean;
  /** Fallback: Resources.Common.NoItems */
  emptyMessage?: string;
}

const DetailSectionComponent = ({
  title,
  action,
  items,
  isLoading,
  emptyMessage,
}: BlDetailSectionProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: Spacing.M,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: "18px",
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: Spacing.M }}>
          <CircularProgress size={24} />
        </Box>
      ) : !items || items.length === 0 ? (
        <Box
          sx={{
            backgroundColor: theme.colors.backgroundTile,
            borderRadius: Radius.M,
            padding: Spacing.M,
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {emptyMessage ?? t(Resources.Common.NoItems)}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={Spacing.S}>
          {items.map((item) => (
            <SectionItem key={item.id} item={item} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const AVATAR_COLORS = avatarColors;

const getAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface AvatarAdornmentProps {
  firstName: string;
  lastName: string;
  src?: string | null;
  size?: number;
}

const AvatarAdornment = ({
  firstName,
  lastName,
  src,
  size = 36,
}: AvatarAdornmentProps): React.ReactNode => {
  const fullName = `${firstName} ${lastName}`;
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <Avatar
      src={src ?? undefined}
      sx={{
        width: size,
        height: size,
        backgroundColor: getAvatarColor(fullName),
        fontSize: "13px",
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </Avatar>
  );
};

interface StatusCircleProps {
  color: string;
  children: React.ReactNode;
  label?: string;
}

const StatusCircle = ({
  color,
  children,
  label,
}: StatusCircleProps): React.ReactNode => {
  const circle = (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );

  if (label) {
    return (
      <Tooltip title={label} placement="bottom" arrow>
        <Box component="span" sx={{ display: "flex", flexShrink: 0 }}>
          {circle}
        </Box>
      </Tooltip>
    );
  }

  return circle;
};

interface IconAdornmentProps {
  children: React.ReactNode;
  color?: string;
}

const IconAdornment = ({
  children,
  color,
}: IconAdornmentProps): React.ReactNode => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        color: color ?? theme.palette.primary.main,
      }}
    >
      {children}
    </Box>
  );
};

interface MetaTextProps {
  children: React.ReactNode;
}

const MetaText = ({ children }: MetaTextProps): React.ReactNode => {
  const theme = useTheme();

  return (
    <Typography
      sx={{
        fontSize: "13px",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {children}
    </Typography>
  );
};

const SectionItem = ({
  item,
}: {
  item: BlDetailSectionItem;
}): React.ReactNode => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.colors.backgroundTile,
        borderRadius: Radius.M,
        py: Spacing.S,
        px: Spacing.M,
        display: "flex",
        alignItems: "center",
        gap: Spacing.S,
        cursor: item.onClick ? "pointer" : "default",
        ...(item.onClick && { "&:hover": { filter: "brightness(1.08)" } }),
      }}
      onClick={item.onClick}
    >
      {item.startAdornment !== undefined && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {item.startAdornment}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          title={item.label.title}
          sx={{
            fontSize: "14px",
            cursor: item.label.onClick ? "pointer" : "inherit",
            ...(item.label.onClick && {
              "&:hover": { textDecoration: "underline" },
            }),
          }}
          onClick={item.label.onClick}
        >
          {item.label.text}
          {item.label.secondaryText && (
            <Typography
              component="span"
              fontSize={12}
              sx={{ color: theme.palette.text.secondary }}
            >
              {" "}
              {item.label.secondaryText}
            </Typography>
          )}
        </Typography>
      </Box>

      {item.endAdornment !== undefined && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {item.endAdornment}
        </Box>
      )}
    </Box>
  );
};

export const DetailSection = Object.assign(DetailSectionComponent, {
  AvatarAdornment,
  StatusCircle,
  IconAdornment,
  MetaText,
});
